import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_VOID } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/invincible.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/invincible.mp3',
  attribution: '"Invincible" by DEAF KEV [NCS Release] - https://ncs.io/invincible',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('invincible', variant),
    slug: buildVariantSlug('invincible', variant),
    title: buildVariantTitle('Invincible', variant),
    description:
      'Hardest. 174 BPM hands-up DnB classic. Dense main drop with rapid clusters - veterans only.',
    bpm: onsets.bpm,
    difficulty: 5,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_VOID,
    audio,
  });

  // 60s @ 174 BPM ≈ 174 beats. The cut starts mid-drop, so we treat the
  // whole thing as drop / break / final-drop.
  builder.section({ startBeat: 0, endBeat: 32, kind: 'drop' });
  builder.section({ startBeat: 32, endBeat: 48, kind: 'bridge' });
  builder.section({ startBeat: 48, endBeat: 128, kind: 'drop' });
  builder.section({ startBeat: 128, endBeat: 174, kind: 'drop' });

  // Drop 1 (cold open): every kick + every strong snare. Sets the tempo.
  // Global 0.5-beat floor across the merge so kick+snare can't accidentally
  // produce 86ms double-taps at 174 BPM.
  //
  // Skip beat 0: the lead-in count-in's "GO!" overlay covers the centre
  // of the screen exactly as a beat-0 pad reaches the player, so it's
  // effectively unhittable. Starting at beat 1 lets the countdown clear
  // before the first pad arrives.
  const drop1 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 1,
      untilBeat: 32,
      kinds: ['kick'],
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 1,
      untilBeat: 32,
      kinds: ['snare'],
      minIntensity: 0.5,
      minSpacing: 0.5,
    }),
  );

  // Bridge: snare backbeat only. Brief breather - same tempo, less density.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 32,
    untilBeat: 48,
    kinds: ['snare'],
    minIntensity: 0.4,
    minSpacing: 1,
  });

  // Main drop: every kick + every snare + the strongest leads. Tight 0.5
  // minSpacing means rapid clusters during 16th-note rolls. This is the
  // hardest section in any builtin.
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 48,
      untilBeat: 128,
      kinds: ['kick', 'snare'],
      minIntensity: 0.3,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 48,
      untilBeat: 128,
      kinds: ['lead'],
      minIntensity: 0.6,
      minSpacing: 1,
    }),
  );

  // Final phrase: pull density back slightly so the player can finish.
  const finalPads = variantSelectBeats(variant, onsets, {
    fromBeat: 128,
    untilBeat: 172,
    kinds: ['kick', 'snare'],
    minIntensity: 0.35,
    minSpacing: 0.75,
  });

  const padBeats = mergeBeats(drop1, bridgePads, drop2, finalPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  for (const c of findBurstChambers(padBeats, {
    fromBeat: 0,
    untilBeat: 172,
    minGapBeats: 8,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(174);
  return builder.build();
}

export const INVINCIBLE_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const INVINCIBLE_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(INVINCIBLE_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
